import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Instagram, Music2, Trophy, Sparkles, ArrowRight, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiscoveryPage = () => {
  const { user, token } = useAuth();
  const [artists, setArtists] = useState([]);
  const [topOfWeek, setTopOfWeek] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [votingId, setVotingId] = useState(null);

  const authHeader = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const fetchAll = useCallback(async () => {
    try {
      await axios.post(`${API}/seed-artists`).catch(() => {});
      const [artistsRes, topRes, featuredRes] = await Promise.all([
        axios.get(`${API}/artists`, { headers: authHeader() }),
        axios.get(`${API}/artists/top-of-week`, { headers: authHeader() }),
        axios.get(`${API}/artists/featured`, { headers: authHeader() }),
      ]);
      setArtists(artistsRes.data);
      setTopOfWeek(topRes.data);
      setFeatured(featuredRes.data);
    } catch (e) {
      console.error('Discovery load error', e);
      toast.error('Failed to load discovery feed');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleUpvote = async (artist) => {
    if (!user) {
      toast('Sign in to upvote your favourites', { icon: '🔒' });
      return;
    }
    if (votingId === artist.id) return;
    setVotingId(artist.id);

    // Optimistic update
    const willVote = !artist.user_has_voted;
    setArtists((prev) =>
      prev.map((a) =>
        a.id === artist.id
          ? {
              ...a,
              user_has_voted: willVote,
              total_upvotes: a.total_upvotes + (willVote ? 1 : -1),
              weekly_upvotes: Math.max(0, a.weekly_upvotes + (willVote ? 1 : -1)),
            }
          : a
      )
    );

    try {
      const res = await axios.post(
        `${API}/artists/${artist.id}/upvote`,
        {},
        { headers: authHeader() }
      );
      // Sync with server counts (handles race conditions)
      setArtists((prev) =>
        prev.map((a) =>
          a.id === artist.id
            ? {
                ...a,
                user_has_voted: res.data.user_has_voted,
                total_upvotes: res.data.total_upvotes,
                weekly_upvotes: res.data.weekly_upvotes,
              }
            : a
        )
      );
      // Refresh "top of week" since rankings can shift
      const topRes = await axios.get(`${API}/artists/top-of-week`, { headers: authHeader() });
      setTopOfWeek(topRes.data);
      toast.success(res.data.user_has_voted ? 'Upvoted!' : 'Vote removed');
    } catch (e) {
      // Revert
      setArtists((prev) =>
        prev.map((a) =>
          a.id === artist.id
            ? {
                ...a,
                user_has_voted: artist.user_has_voted,
                total_upvotes: artist.total_upvotes,
                weekly_upvotes: artist.weekly_upvotes,
              }
            : a
        )
      );
      toast.error(e.response?.data?.detail || 'Vote failed');
    } finally {
      setVotingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#B5FF00] font-mono uppercase tracking-widest animate-pulse">
          Loading discovery...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="discovery-page">
      <Seo title="Discovery — Genres, Artists & Venues" description="Browse the underground by mood, genre, or artist. Curated lineups from every corner of the electronic music scene in the UK and India." path="/discovery" />
      {/* HERO — Top Trending Artist */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden" data-testid="discovery-hero">
        {topOfWeek ? (
          <>
            <div className="absolute inset-0">
              <img
                src={topOfWeek.image_url}
                alt={topOfWeek.name}
                className="w-full h-full object-cover img-grayscale"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/85 to-[#050505]/40" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/90 via-transparent to-transparent" />
            </div>

            <div className="relative container mx-auto px-4 md:px-8 pb-16 md:pb-24 pt-32">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 mb-6 border border-[#B5FF00]/40 bg-[#B5FF00]/5 px-3 py-2">
                  <Trophy size={16} className="text-[#B5FF00]" strokeWidth={1.5} />
                  <span className="font-mono text-xs tracking-widest text-[#B5FF00]">
                    TOP TRENDING ARTIST OF THE WEEK
                  </span>
                </div>

                <h1
                  className="font-['Josefin_Sans'] text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-none mb-6"
                  data-testid="top-artist-name"
                >
                  {topOfWeek.name}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mb-6">
                  {topOfWeek.city && (
                    <span className="flex items-center gap-2 text-zinc-400 font-mono text-sm">
                      <MapPin size={14} className="text-[#B5FF00]" />
                      {topOfWeek.city.toUpperCase()}
                    </span>
                  )}
                  {topOfWeek.genres?.map((g) => (
                    <span
                      key={g}
                      className="font-mono text-xs tracking-widest text-[#B5FF00] border border-[#B5FF00]/30 px-3 py-1"
                    >
                      {g.toUpperCase()}
                    </span>
                  ))}
                </div>

                <p className="text-zinc-300 text-lg max-w-2xl mb-8 leading-relaxed">
                  {topOfWeek.bio}
                </p>

                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-3 px-4 py-3 bg-[#B5FF00]/10 border border-[#B5FF00]/40">
                    <Heart size={18} className="text-[#B5FF00] fill-[#B5FF00]" />
                    <span className="font-mono text-sm text-white">
                      <span className="text-[#B5FF00] font-bold text-lg">{topOfWeek.weekly_upvotes}</span> votes this week
                    </span>
                  </div>
                  {topOfWeek.instagram_url && (
                    <a
                      href={topOfWeek.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-3 border border-zinc-700 text-white hover:border-[#B5FF00] transition-colors"
                      data-testid="top-artist-instagram"
                    >
                      <Instagram size={18} />
                      <span className="font-mono text-xs tracking-widest">INSTAGRAM</span>
                    </a>
                  )}
                  {topOfWeek.spotify_url && (
                    <a
                      href={topOfWeek.spotify_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-3 border border-zinc-700 text-white hover:border-[#B5FF00] transition-colors"
                      data-testid="top-artist-spotify"
                    >
                      <Music2 size={18} />
                      <span className="font-mono text-xs tracking-widest">SPOTIFY</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="relative container mx-auto px-4 md:px-8 py-32 text-center">
            <Trophy size={48} className="text-[#B5FF00] mx-auto mb-6" strokeWidth={1} />
            <h1 className="font-['Josefin_Sans'] text-4xl md:text-6xl font-bold text-white mb-4">
              DISCOVERY
            </h1>
            <p className="text-zinc-400 max-w-xl mx-auto">
              Upvotes are still rolling in. Be the first to crown this week's top trending artist.
            </p>
          </div>
        )}
      </section>

      {/* FEATURED ARTIST (editorial pick) */}
      {featured && (
        <section className="py-20 border-t border-zinc-800 bg-[#0A0A0A]" data-testid="featured-artist-section">
          <div className="container mx-auto px-4 md:px-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-[#B5FF00]" />
              <span className="font-mono text-xs tracking-widest text-[#B5FF00]">FEATURED ARTIST</span>
            </div>
            <h2 className="font-['Josefin_Sans'] text-3xl md:text-5xl font-bold text-white mb-10">
              ARTIST OF THE WEEK
            </h2>
            <ArtistSpotlight
              artist={featured}
              onUpvote={handleUpvote}
              isVoting={votingId === featured.id}
            />
          </div>
        </section>
      )}

      {/* LEADERBOARD */}
      <section className="py-20 border-t border-zinc-800" data-testid="leaderboard-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2 block">
                WEEKLY RANKINGS
              </span>
              <h2 className="font-['Josefin_Sans'] text-4xl md:text-5xl font-bold text-white">
                ALL ARTISTS
              </h2>
              <p className="text-zinc-500 mt-2 max-w-lg">
                Underground talent from across India. Upvote your favourites — the artist with the most weekly votes earns the crown.
              </p>
            </div>
          </div>

          {artists.length === 0 ? (
            <div className="text-center py-16 border border-zinc-800">
              <p className="text-zinc-500 font-mono">No artists yet. Check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="artists-grid">
              {artists.map((artist, idx) => (
                <ArtistCard
                  key={artist.id}
                  artist={artist}
                  rank={idx + 1}
                  onUpvote={handleUpvote}
                  isVoting={votingId === artist.id}
                />
              ))}
            </div>
          )}

          {!user && (
            <div className="mt-12 text-center border border-zinc-800 bg-[#0A0A0A] p-8">
              <p className="text-zinc-300 mb-4 font-mono text-sm tracking-wider">
                SIGN IN TO UPVOTE ARTISTS
              </p>
              <Link to="/login" className="btn-primary inline-flex items-center gap-2">
                SIGN IN <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span className="footer-brand">UNIGHT</span>
        </div>
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_venue-pulse-14/artifacts/8f7het1z_Unight%202d%20Logos%20%28TM%29-05.png"
              alt="Unight"
              className="h-8"
            />
            <span className="font-mono text-xs text-zinc-600 tracking-widest">
              © 2026 UNIGHT. ALL RIGHTS RESERVED.
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

const ArtistSpotlight = ({ artist, onUpvote, isVoting }) => (
  <div
    className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-zinc-800"
    data-testid={`featured-spotlight-${artist.id}`}
  >
    <div className="relative h-80 md:h-auto md:min-h-[400px] overflow-hidden">
      <img src={artist.image_url} alt={artist.name} className="w-full h-full object-cover img-grayscale" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 to-transparent md:bg-gradient-to-r" />
    </div>
    <div className="p-8 md:p-10 flex flex-col">
      <h3 className="font-['Josefin_Sans'] text-3xl md:text-4xl font-bold text-white mb-3">
        {artist.name}
      </h3>
      <div className="flex flex-wrap gap-2 mb-4">
        {artist.genres?.map((g) => (
          <span key={g} className="font-mono text-xs tracking-widest text-[#B5FF00] border border-[#B5FF00]/30 px-2 py-1">
            {g.toUpperCase()}
          </span>
        ))}
      </div>
      <p className="text-zinc-400 leading-relaxed mb-6 flex-1">{artist.bio}</p>
      <div className="flex flex-wrap items-center gap-3">
        <UpvoteButton artist={artist} onUpvote={onUpvote} isVoting={isVoting} />
        {artist.instagram_url && (
          <a
            href={artist.instagram_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-3 border border-zinc-700 text-white hover:border-[#B5FF00] hover:text-[#B5FF00] transition-colors"
            aria-label="Instagram"
            data-testid={`featured-instagram-${artist.id}`}
          >
            <Instagram size={18} />
          </a>
        )}
        {artist.spotify_url && (
          <a
            href={artist.spotify_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-3 border border-zinc-700 text-white hover:border-[#B5FF00] hover:text-[#B5FF00] transition-colors"
            aria-label="Spotify"
            data-testid={`featured-spotify-${artist.id}`}
          >
            <Music2 size={18} />
          </a>
        )}
      </div>
    </div>
  </div>
);

const ArtistCard = ({ artist, rank, onUpvote, isVoting }) => (
  <div
    className="group relative border border-zinc-800 hover:border-[#B5FF00]/40 transition-all duration-300 overflow-hidden bg-[#0A0A0A]"
    data-testid={`artist-card-${artist.id}`}
  >
    <div className="relative h-64 overflow-hidden">
      <img
        src={artist.image_url}
        alt={artist.name}
        className="w-full h-full object-cover img-grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
      {rank <= 3 && (
        <div className="absolute top-3 left-3 bg-[#050505]/90 border border-[#B5FF00]/40 px-2 py-1">
          <span className="font-mono text-xs text-[#B5FF00] tracking-widest">
            #{rank} {rank === 1 ? 'TRENDING' : ''}
          </span>
        </div>
      )}
    </div>

    <div className="p-5">
      <h3 className="font-['Josefin_Sans'] text-2xl text-white mb-1 group-hover:text-[#B5FF00] transition-colors">
        {artist.name}
      </h3>
      {artist.city && (
        <p className="text-zinc-500 text-sm flex items-center gap-1.5 mb-3">
          <MapPin size={12} />
          {artist.city}
        </p>
      )}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {artist.genres?.slice(0, 3).map((g) => (
          <span key={g} className="font-mono text-[10px] tracking-widest text-zinc-400 border border-zinc-700 px-2 py-0.5">
            {g.toUpperCase()}
          </span>
        ))}
      </div>
      <p className="text-zinc-500 text-sm line-clamp-2 mb-5">{artist.bio}</p>

      <div className="flex items-center justify-between gap-3">
        <UpvoteButton artist={artist} onUpvote={onUpvote} isVoting={isVoting} compact />
        <div className="flex items-center gap-2">
          {artist.instagram_url && (
            <a
              href={artist.instagram_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-zinc-400 hover:text-[#B5FF00] transition-colors"
              aria-label="Instagram"
              data-testid={`instagram-link-${artist.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Instagram size={18} />
            </a>
          )}
          {artist.spotify_url && (
            <a
              href={artist.spotify_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-zinc-400 hover:text-[#B5FF00] transition-colors"
              aria-label="Spotify"
              data-testid={`spotify-link-${artist.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Music2 size={18} />
            </a>
          )}
        </div>
      </div>
    </div>
  </div>
);

const UpvoteButton = ({ artist, onUpvote, isVoting, compact = false }) => (
  <button
    onClick={() => onUpvote(artist)}
    disabled={isVoting}
    className={`flex items-center gap-2 ${compact ? 'px-3 py-2' : 'px-4 py-2.5'} border transition-all ${
      artist.user_has_voted
        ? 'bg-[#B5FF00]/10 border-[#B5FF00] text-[#B5FF00]'
        : 'border-zinc-700 text-zinc-300 hover:border-[#B5FF00] hover:text-[#B5FF00]'
    } ${isVoting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    data-testid={`upvote-btn-${artist.id}`}
    aria-pressed={artist.user_has_voted}
  >
    <Heart
      size={compact ? 16 : 18}
      strokeWidth={2}
      className={artist.user_has_voted ? 'fill-[#B5FF00]' : ''}
    />
    <span className="font-mono text-sm font-semibold" data-testid={`upvote-count-${artist.id}`}>
      {artist.total_upvotes}
    </span>
    {!compact && (
      <span className="font-mono text-xs tracking-widest opacity-70">
        {artist.weekly_upvotes} THIS WEEK
      </span>
    )}
  </button>
);

export default DiscoveryPage;
